import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CustomCalendar = ({
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  onClose,
  isRangeSelection = false,
  minDate = new Date(),
  pickupDate = null,
  isMobile = false,
  singleMonth = false,
}) => {
  const getInitialDate = () => {
    if (selectedStartDate) {
      return new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), 1);
    }
    return new Date();
  };

  const [currentDate, setCurrentDate] = useState(getInitialDate);
  const [hoveredDate, setHoveredDate] = useState(null);
  const markerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null);

  const computePanelPos = () => {
    if (!markerRef.current) return;
    const rect = markerRef.current.getBoundingClientRect();
    setPanelPos({ left: rect.left, top: rect.top });
  };

  useLayoutEffect(() => {
    computePanelPos();
    const onReposition = () => computePanelPos();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, []);

  // Second pass, once the panel has actually rendered at its real size:
  // clamp it fully inside the viewport instead of letting it run off
  // the right edge or bottom of the screen.
  useLayoutEffect(() => {
    if (!panelPos || !panelRef.current) return;
    const panelRect = panelRef.current.getBoundingClientRect();
    let { left, top } = panelPos;
    let changed = false;

    const maxLeft = window.innerWidth - panelRect.width - 8;
    if (left > maxLeft) {
      left = Math.max(8, maxLeft);
      changed = true;
    } else if (left < 8) {
      left = 8;
      changed = true;
    }

    const maxTop = window.innerHeight - panelRect.height - 8;
    if (top > maxTop) {
      top = Math.max(8, maxTop);
      changed = true;
    }

    if (changed) setPanelPos({ left, top });
  }, [panelPos]);

  useEffect(() => {
    if (selectedStartDate) {
      setCurrentDate(new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), 1));
    }
  }, [selectedStartDate]);

  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const firstDayOfNextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const normalizeDate = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const isDateDisabled = (date) => {
    if (!date) return true;
    return normalizeDate(date) < normalizeDate(minDate);
  };

  const isPrevMonthDisabled = () => {
    const today = new Date();
    return (
      currentDate.getFullYear() === today.getFullYear() &&
      currentDate.getMonth() === today.getMonth()
    );
  };

  const goToPrevMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const startIndex = (firstDay.getDay() + 6) % 7;
    for (let i = 0; i < startIndex; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  };

  const handleDateClick = (date, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDateDisabled(date)) return;
    if (isRangeSelection) {
      if (!selectedStartDate) {
        onDateSelect(date, null);
      } else if (!selectedEndDate) {
        if (date >= selectedStartDate) {
          onDateSelect(selectedStartDate, date);
        } else {
          onDateSelect(date, selectedStartDate);
        }
      } else {
        onDateSelect(date, null);
      }
    } else {
      onDateSelect(date);
    }
  };

  const isStartDate = (date) => {
    if (!selectedStartDate || !date) return false;
    return date.getTime() === selectedStartDate.getTime();
  };

  const isEndDate = (date) => {
    if (!selectedEndDate || !date) return false;
    return date.getTime() === selectedEndDate.getTime();
  };

  const isPickupDate = (date) => {
    if (!pickupDate || !date) return false;
    return date.getTime() === pickupDate.getTime();
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !date || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateInPickupRange = (date) => {
    if (!pickupDate || !selectedStartDate || !date) return false;
    const start = pickupDate < selectedStartDate ? pickupDate : selectedStartDate;
    const end = pickupDate < selectedStartDate ? selectedStartDate : pickupDate;
    return date >= start && date <= end;
  };

  const handleMouseEnter = (date) => {
    if (isRangeSelection && selectedStartDate && !selectedEndDate) {
      setHoveredDate(date);
    } else if (!isRangeSelection && pickupDate && selectedStartDate) {
      setHoveredDate(date);
    }
  };

  const handleMouseLeave = () => setHoveredDate(null);

  const isDateInHoverRange = (date) => {
    if (!date) return false;
    if (isRangeSelection && selectedStartDate && !selectedEndDate && hoveredDate) {
      const start = selectedStartDate < hoveredDate ? selectedStartDate : hoveredDate;
      const end = selectedStartDate < hoveredDate ? hoveredDate : selectedStartDate;
      return date >= start && date <= end;
    } else if (!isRangeSelection && pickupDate && selectedStartDate && hoveredDate) {
      const start = pickupDate < hoveredDate ? pickupDate : hoveredDate;
      const end = pickupDate < hoveredDate ? hoveredDate : pickupDate;
      return date >= start && date <= end;
    }
    return false;
  };

  // ---------------------------------------------------------------------------
  // Render a single month grid — with continuous range strip
  // ---------------------------------------------------------------------------
  const renderMonth = (date) => {
    const days = generateCalendarDays(date);

    return (
      <div className="w-full flex flex-col items-center">
        {/* Day headers */}
        <div className="grid grid-cols-7 w-full mb-1">
          {dayNames.map((day) => (
            <div
              key={day}
              className="h-9 flex items-center justify-center text-[11px] font-heading font-bold text-text-muted uppercase tracking-wider"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 w-full">
          {days.map((d, index) => {
            if (!d) {
              return <div key={index} className="h-9" />;
            }

            const inRange = isDateInRange(d) || isDateInPickupRange(d) || isDateInHoverRange(d);
            const isStart = isStartDate(d);
            const isEnd = isEndDate(d);
            const isPickup = isPickupDate(d);
            const disabled = isDateDisabled(d);
            const today = isToday(d);
            const isSelected = isStart || isEnd || isPickup;

            // Strip background — full width for middle, half for edges
            let stripClass = "absolute inset-y-0 bg-primary/15 ";
            const showStrip = inRange || isSelected;

            if (isStart) stripClass += "left-1/2 right-0 ";
            else if (isEnd) stripClass += "left-0 right-1/2 ";
            else if (inRange) stripClass += "left-0 right-0 ";

            // Circle on top
            let circleClass = "relative z-10 w-9 h-9 flex items-center justify-center text-sm font-body font-medium rounded-full transition-all duration-150 ";

            if (disabled) {
              circleClass += "text-text-light cursor-not-allowed opacity-40";
            } else if (isSelected) {
              circleClass += "bg-primary text-white font-bold cursor-pointer ";
            } else if (inRange) {
              circleClass += "text-dark cursor-pointer hover:bg-primary/20 ";
            } else if (today) {
              circleClass += "border border-primary text-primary cursor-pointer ";
            } else {
              circleClass += "text-dark cursor-pointer hover:bg-surface-raised ";
            }

            return (
              <div
                key={index}
                className="relative h-9 flex items-center justify-center"
                onClick={(e) => !disabled && handleDateClick(d, e)}
                onMouseEnter={() => !disabled && handleMouseEnter(d)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Continuous strip */}
                {showStrip && <div className={stripClass} />}
                {/* Date circle */}
                <div className={circleClass}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Close on outside click
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".custom-calendar")) return;
      if (event.target.closest("[data-calendar-trigger]")) return;
      if (onClose) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const panel = (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        left: panelPos?.left,
        top: panelPos?.top,
        zIndex: 9999,
        visibility: panelPos ? "visible" : "hidden",
      }}
      className={`custom-calendar bg-surface border border-surface-border rounded-2xl shadow-card p-5 ${
        singleMonth ? "w-[300px]" : isMobile ? "w-[300px]" : "w-[620px]"
      }`}
      onClick={handleCalendarClick}
    >
      {/* Navigation header */}
      {!singleMonth && !isMobile ? (
        <div className="grid grid-cols-2 gap-8 mb-4">
          {/* Left month header */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={goToPrevMonth}
              disabled={isPrevMonthDisabled()}
              className={`p-1.5 rounded-lg transition-colors ${
                isPrevMonthDisabled() ? "opacity-30 cursor-not-allowed" : "hover:bg-surface-raised"
              }`}
            >
              <ChevronLeft className="w-4 h-4 text-text-muted" />
            </button>
            <span className="text-sm font-heading font-bold text-dark tracking-wide">
              {monthNames[firstDayOfMonth.getMonth()]} {firstDayOfMonth.getFullYear()}
            </span>
            <div className="w-7" />
          </div>
          {/* Right month header */}
          <div className="flex items-center justify-between">
            <div className="w-7" />
            <span className="text-sm font-heading font-bold text-dark tracking-wide">
              {monthNames[firstDayOfNextMonth.getMonth()]} {firstDayOfNextMonth.getFullYear()}
            </span>
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-1.5 hover:bg-surface-raised rounded-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-text-muted" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={goToPrevMonth}
            disabled={isPrevMonthDisabled()}
            className={`p-1.5 rounded-lg transition-colors ${
              isPrevMonthDisabled() ? "opacity-30 cursor-not-allowed" : "hover:bg-surface-raised"
            }`}
          >
            <ChevronLeft className="w-4 h-4 text-text-muted" />
          </button>
          <span className="text-sm font-heading font-bold text-dark tracking-wide">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button
            type="button"
            onClick={goToNextMonth}
            className="p-1.5 hover:bg-surface-raised rounded-lg transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-text-muted" />
          </button>
        </div>
      )}

      {/* Month(s) */}
      <div className={`grid ${!singleMonth && !isMobile ? "grid-cols-2 gap-8" : "grid-cols-1"}`}>
        {renderMonth(firstDayOfMonth)}
        {!singleMonth && !isMobile && renderMonth(firstDayOfNextMonth)}
      </div>
    </div>
  );

  return (
    <>
      {/* Invisible marker left in the original spot (inside Form.js's
          own "absolute top-full ... mt-2" wrapper) purely so we can read
          its on-screen position — the actual visible panel above is
          portaled to <body> and fixed-positioned from that point. */}
      <span
        ref={markerRef}
        style={{ display: "block", width: 0, height: 0, overflow: "hidden" }}
      />
      {createPortal(panel, document.body)}
    </>
  );
};

export default CustomCalendar;